import { useState, useEffect, Children, isValidElement } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type Me } from './lib/api'
import {
  IconActivity, IconAlert, IconDomains, IconInfo, IconKeys, IconLogout, IconMark,
  IconAdmin, IconInbound, IconMessages, IconOverview, IconSend, IconSmtp, IconSuppress, IconTeam, IconWebhook,
  IconTemplates, IconStreams, IconRoutes, IconHealth, IconBilling, IconSettings,
} from './components/Icons'
import { NotificationBell } from './components/NotificationBell'
import { ThemeToggle } from './components/ThemeToggle'
import { Login, Signup, ResetPassword } from './pages/Auth'
import { Domains, DomainDetail } from './pages/Domains'
import { Overview } from './pages/Overview'
import { ApiKeys } from './pages/ApiKeys'
import { Messages, MessageDetail } from './pages/Messages'
import { Reporting } from './pages/Reporting'
import { ContentAdmin } from './pages/ContentAdmin'
import { ToolLeads } from './pages/ToolLeads'
import { MailTest } from './pages/MailTest'
import { Smtp } from './pages/Smtp'
import { Suppressions } from './pages/Suppressions'
import { Webhooks } from './pages/Webhooks'
import { Activity } from './pages/Activity'
import { Team } from './pages/Team'
import { Send } from './pages/Send'
import { AdminOverview, AdminTenants, AdminTenantDetail } from './pages/Admin'
import { Inbound, InboundDetail } from './pages/Inbound'
import { Settings } from './pages/Settings'
import { Templates, TemplateDetailPage } from './pages/Templates'
import { Streams } from './pages/Streams'
import { InboundRoutes } from './pages/Routes'
import { Health } from './pages/Health'
import { AdminIps } from './pages/AdminIps'
import { Rebound } from './pages/Rebound'
import { AdminAudit } from './pages/AdminAudit'
import { Billing } from './pages/Billing'
import { Mailboxes } from './pages/Mailboxes'
import { Mail, MailDetail, Compose } from './pages/Mail'
import { Lists, ListDetail } from './pages/Audience'
import { Campaigns, CampaignDetail } from './pages/Campaigns'
import { Wordmark } from './components/AuthAside'
import { EmailAccounts } from './pages/EmailAccounts'
import { NavCount, useOperatorAlerts } from './components/OperatorAlerts'
import { Verification, SendingLimits, AdminVerifications, AdminVerificationDetail } from './pages/Verification'

const CRUMBS: Record<string, string> = {
  '/': 'Overview',
  '/domains': 'Domains',
  '/health': 'Health',
  '/send': 'Send',
  '/templates': 'Templates',
  '/streams': 'Streams',
  '/inbound': 'Received',
  '/routes': 'Inbound routes',
  '/messages': 'Messages',
  '/suppressions': 'Suppressions',
  '/rebound': 'Rebound',
  '/api-keys': 'API keys',
  '/smtp': 'SMTP credentials',
  '/webhooks': 'Webhooks',
  '/activity': 'Activity',
  '/team': 'Team',
  '/settings': 'Account settings',
  '/billing': 'Billing',
  '/admin': 'Operator',
  '/admin/tenants': 'Operator / Tenants',
  '/admin/verifications': 'Operator / Verifications',
  '/verification': 'Verification',
  '/email-accounts': 'Email accounts',
  '/limits': 'Sending limits',
  '/admin/ips': 'Operator / Sending fleet',
  '/mail': 'Mail',
  '/mailboxes': 'Mailboxes',
  '/lists': 'Lists',
  '/campaigns': 'Campaigns',
}

export function App() {
  const location = useLocation()

  const { data: me, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: api.auth.me,
    retry: false,
  })

  const unauthenticated = error instanceof ApiError && error.status === 401

  if (isLoading) {
    // Neutral boot loader — deliberately NOT the auth layout, so a hard refresh while signed
    // in never flashes the login/brand panel before the dashboard resolves.
    return (
      <div className="app-boot">
        <div className="app-boot-mark"><IconMark /></div>
        <div className="app-boot-bar"><span /></div>
      </div>
    )
  }

  if (unauthenticated || !me) {
    const onAuthRoute = location.pathname === '/signup' || location.pathname === '/login'
      || location.pathname === '/reset-password'
    return (
      <Routes>
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to={onAuthRoute ? location.pathname : '/login'} replace />} />
      </Routes>
    )
  }

  const crumb = CRUMBS[location.pathname]
    ?? (location.pathname.startsWith('/domains/') ? 'Domains / detail'
      : location.pathname.startsWith('/messages/') ? 'Messages / detail' : '')

  // Owner-approval gate: until an operator approves the account it only gets identity
  // verification, domain setup and its own settings. Sending stays closed server-side too.
  // The platform operator (SuperAdmin) is never gated.
  if (me.tenant.status === 'pending_review' && me.user.role !== 'SuperAdmin') {
    return (
      <AppShell me={me} crumb={crumb} limited>
        <Routes>
          <Route path="/verification" element={<Verification />} />
          <Route path="/domains" element={<Domains me={me} />} />
          <Route path="/domains/:id" element={<DomainDetail />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/verification" replace />} />
        </Routes>
      </AppShell>
    )
  }

  return (
    <AppShell me={me} crumb={crumb}>
      <Routes>
        <Route path="/" element={<Overview me={me} />} />
        <Route path="/domains" element={<Domains me={me} />} />
        <Route path="/health" element={<Health />} />
        <Route path="/domains/:id" element={<DomainDetail />} />
        <Route path="/send" element={<Send me={me} />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/templates/new" element={<TemplateDetailPage />} />
        <Route path="/templates/:id" element={<TemplateDetailPage />} />
        <Route path="/streams" element={<Streams />} />
        <Route path="/inbound" element={<Inbound me={me} />} />
        <Route path="/inbound/:id" element={<InboundDetail />} />
        <Route path="/routes" element={<InboundRoutes />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:id" element={<MessageDetail />} />
        <Route path="/reporting" element={<Reporting />} />
        <Route path="/tester" element={<MailTest />} />
        <Route path="/suppressions" element={<Suppressions />} />
        <Route path="/rebound" element={<Rebound />} />
        <Route path="/api-keys" element={<ApiKeys />} />
        <Route path="/smtp" element={<Smtp />} />
        <Route path="/webhooks" element={<Webhooks />} />
        <Route path="/activity" element={<Activity me={me} />} />
        <Route path="/team" element={<Team me={me} />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/verification" element={<Verification />} />
        <Route path="/limits" element={<SendingLimits />} />
        <Route path="/email-accounts" element={<EmailAccounts />} />
        <Route path="/admin/verifications" element={<AdminVerifications />} />
        <Route path="/admin/verifications/:id" element={<AdminVerificationDetail />} />
        <Route path="/admin" element={<AdminOverview me={me} />} />
        <Route path="/admin/tenants" element={<AdminTenants />} />
        <Route path="/admin/tenants/:id" element={<AdminTenantDetail />} />
        <Route path="/admin/ips" element={<AdminIps />} />
        <Route path="/admin/content" element={<ContentAdmin />} />
        <Route path="/admin/tool-leads" element={<ToolLeads />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
        <Route path="/mail" element={<Mail />} />
        <Route path="/mail/compose" element={<Compose />} />
        <Route path="/mail/:id" element={<MailDetail />} />
        <Route path="/mailboxes" element={<Mailboxes />} />
        <Route path="/lists" element={<Lists />} />
        <Route path="/lists/:id" element={<ListDetail />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/signup" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  )
}

// The chrome (sidebar + topbar + collapsible/off-canvas behaviour) lives here so the toggle
// state is local and does not re-run the auth query on every open/close.
function AppShell({ me, crumb, children, limited }: { me: Me; crumb: string; children: React.ReactNode; limited?: boolean }) {
  const location = useLocation()
  // Open on desktop, closed on phones, by default. The toggle overrides either way.
  const [navOpen, setNavOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > 900 : true))
  // Close the drawer after navigating on a small screen.
  useEffect(() => { if (window.innerWidth <= 900) setNavOpen(false) }, [location.pathname])

  return (
    <div className={`shell ${navOpen ? 'nav-open' : 'nav-closed'}`}>
      <Sidebar email={me.user.email} role={me.user.role} limited={limited} canMarket={me.tenant.status === 'active'} />
      <div className="nav-backdrop" onClick={() => setNavOpen(false)} />
      <div className="main">
        <div className="topbar">
          <button className="nav-toggle" aria-label="Toggle sidebar" onClick={() => setNavOpen(o => !o)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
          <span className="crumb">{crumb}</span>
          <div className="topbar-right">
            <ThemeToggle />
            <NotificationBell />
            <AccountPill me={me} />
          </div>
        </div>
        <div className="content">
          <AccountBanner me={me} />
          {children}
        </div>
      </div>
    </div>
  )
}

/**
 * A collapsible sidebar section. Click the header to open/close; the choice is remembered per
 * section. The section that contains the page you are on is always kept open, so collapsing a
 * group never hides where you actually are.
 */
function NavGroup({ title, children }: { title: string; children: React.ReactNode }) {
  const location = useLocation()
  const storeKey = `iq-nav-${title}`

  const hasActive = Children.toArray(children).some(c => {
    if (!isValidElement(c)) return false
    const to = (c.props as { to?: string }).to
    if (!to) return false
    return to === '/' ? location.pathname === '/' : (location.pathname === to || location.pathname.startsWith(to + '/'))
  })

  const [open, setOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(storeKey)
    return saved == null ? true : saved === '1'
  })
  const expanded = open || hasActive

  function toggle() {
    setOpen(o => { const next = !o; localStorage.setItem(storeKey, next ? '1' : '0'); return next })
  }

  return (
    <div className={`nav-group ${expanded ? 'open' : 'closed'}`}>
      <button type="button" className="nav-acc" onClick={toggle} aria-expanded={expanded}>
        <span>{title}</span>
        <svg className="nav-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <nav className="nav">{children}</nav>
    </div>
  )
}

function Sidebar({ email, role, limited, canMarket }: { email?: string; role?: string; limited?: boolean; canMarket?: boolean }) {
  const qc = useQueryClient()
  const { count: kycCount } = useOperatorAlerts()

  async function logout() {
    await api.auth.logout()
    qc.clear()
    window.location.href = '/login'
  }

  return (
    <aside className="sidebar">
      <div className="logo">
        <span className="mark"><IconMark /></span>
        <Wordmark />
      </div>

      <div className="nav-scroll">
        {limited ? (
          <NavGroup title="Get started">
            <NavLink to="/verification"><IconShield /> Verify identity</NavLink>
            <NavLink to="/domains"><IconDomains /> Domains</NavLink>
            <NavLink to="/settings"><IconSettings /> Settings</NavLink>
          </NavGroup>
        ) : (<>
        <NavGroup title="General">
          <NavLink to="/" end><IconOverview /> Overview</NavLink>
          <NavLink to="/health"><IconHealth /> Deliverability</NavLink>
        </NavGroup>

        <NavGroup title="Sending">
          <NavLink to="/send"><IconSend /> Send</NavLink>
          <NavLink to="/domains"><IconDomains /> Domains</NavLink>
          <NavLink to="/templates"><IconTemplates /> Templates</NavLink>
          <NavLink to="/streams"><IconStreams /> Streams</NavLink>
          <NavLink to="/messages"><IconMessages /> Email logs</NavLink>
        </NavGroup>

        <NavGroup title="Reporting">
          <NavLink to="/reporting"><IconOverview /> Reports</NavLink>
        </NavGroup>

        <NavGroup title="Deliverability">
          <NavLink to="/rebound"><IconAlert /> Rebound</NavLink>
          <NavLink to="/suppressions"><IconSuppress /> Suppressions</NavLink>
          <NavLink to="/tester"><IconHealth /> Inbox tester</NavLink>
        </NavGroup>

        <NavGroup title="Inbound">
          <NavLink to="/email-accounts"><IconMessages /> Email accounts</NavLink>
          <NavLink to="/inbound"><IconInbound /> Received</NavLink>
          <NavLink to="/routes"><IconRoutes /> Routes</NavLink>
        </NavGroup>

        <NavGroup title="Integration">
          <NavLink to="/api-keys"><IconKeys /> API keys</NavLink>
          <NavLink to="/smtp"><IconSmtp /> SMTP credentials</NavLink>
          <NavLink to="/webhooks"><IconWebhook /> Webhooks</NavLink>
        </NavGroup>

        <NavGroup title="Account">
          <NavLink to="/team"><IconTeam /> Team</NavLink>
          <NavLink to="/activity"><IconActivity /> Activity</NavLink>
          <NavLink to="/settings"><IconSettings /> Settings</NavLink>
          <NavLink to="/billing"><IconBilling /> Billing</NavLink>
          <NavLink to="/limits"><IconOverview /> Sending limits</NavLink>
          <NavLink to="/verification"><IconShield /> Verification</NavLink>
        </NavGroup>

        {(role === 'SuperAdmin' || canMarket) && (
          <NavGroup title="Marketing">
            <NavLink to="/campaigns"><IconSend /> Campaigns</NavLink>
            <NavLink to="/lists"><IconTeam /> Lists</NavLink>
            {role === 'SuperAdmin' && <NavLink to="/mail"><IconInbound /> Mail</NavLink>}
            {role === 'SuperAdmin' && <NavLink to="/mailboxes"><IconMessages /> Mailboxes</NavLink>}
          </NavGroup>
        )}

        {role === 'SuperAdmin' && (
          <NavGroup title="Operator">
            <NavLink to="/admin" end><IconAdmin /> Console</NavLink>
            <NavLink to="/admin/tenants"><IconTeam /> Tenants</NavLink>
            <NavLink to="/admin/verifications"><IconShield /> Verifications <NavCount n={kycCount} /></NavLink>
            <NavLink to="/admin/ips"><IconSmtp /> Sending fleet</NavLink>
            <NavLink to="/admin/content"><IconTemplates /> Content</NavLink>
            <NavLink to="/admin/tool-leads"><IconActivity /> Tool leads</NavLink>
            <NavLink to="/admin/audit"><IconActivity /> Audit log</NavLink>
          </NavGroup>
        )}
        </>)}
      </div>

      <div className="foot">
        <div className="who">{email}</div>
        <button onClick={logout}><IconLogout /> Sign out</button>
      </div>
    </aside>
  )
}

/** Account chip: avatar + name/role, opening a menu. A sandboxed or pending account must
 *  never be surprised by a 403, so its status is always one click away here. */
function AccountPill({ me }: { me: Me }) {
  const t = me.tenant
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  const [cls, text] =
    t.status === 'suspended' ? ['critical', 'Suspended'] :
    t.status === 'pending_review' ? ['pending', 'Awaiting review'] :
    t.is_sandboxed ? ['info', 'Sandbox'] :
    ['ok', 'Active']

  const email = me.user?.email ?? ''
  const initial = (email[0] ?? 'u').toUpperCase()
  const name = t.name || email
  const role = me.user?.role === 'SuperAdmin' ? 'Operator'
    : me.user?.role ? me.user.role.replace(/^\w/, c => c.toUpperCase()) : 'Member'

  async function logout() {
    await api.auth.logout()
    qc.clear()
    window.location.href = '/login'
  }

  return (
    <div className="acct-wrap">
      <button className="acct-btn" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="true">
        <span className="acct-avatar" title={email}>{initial}</span>
        <span className="acct-id">
          <span className="acct-name">{name}</span>
          <span className="acct-role">{role}</span>
        </span>
        <span className="acct-chev" aria-hidden>▾</span>
      </button>

      {open && (
        <>
          <div className="acct-backdrop" onClick={() => setOpen(false)} />
          <div className="acct-menu">
            <div className="acct-menu-head">
              <span className="acct-avatar lg">{initial}</span>
              <div className="acct-menu-id">
                <strong>{name}</strong>
                <span>{email}</span>
              </div>
            </div>
            <div className="acct-menu-meta">
              {me.plan && <span className="acct-plan">{me.plan.name}</span>}
              <span className={`badge ${cls}`}>{text}</span>
            </div>
            <NavLink to="/settings" onClick={() => setOpen(false)}>Account settings</NavLink>
            <NavLink to="/team" onClick={() => setOpen(false)}>Team</NavLink>
            <NavLink to="/billing" onClick={() => setOpen(false)}>Billing</NavLink>
            <button className="acct-signout" onClick={logout}><IconLogout /> Sign out</button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The failure mode this avoids: a developer integrates fully, gets a 403 on their
 * first real send, and concludes the product is broken. Say it up front instead.
 */
function AccountBanner({ me }: { me: Me }) {
  const t = me.tenant

  if (t.status === 'suspended') {
    return (
      <div className="alert error">
        <span className="ai"><IconAlert /></span>
        <div>
          <strong>Sending is suspended.</strong>{' '}
          {t.suspension_reason ?? 'Contact hello@itsbingmybusiness.com to resolve this.'}
        </div>
      </div>
    )
  }

  if (t.status === 'pending_review') {
    return (
      <div className="alert warn">
        <span className="ai"><IconAlert /></span>
        <div>
          <strong>Your account is awaiting review.</strong> You can add domains, publish
          DNS and verify them now. Until a person approves the account you can only send
          to addresses you have verified yourself — so the whole integration can be
          finished while you wait.
        </div>
      </div>
    )
  }

  if (t.is_sandboxed) {
    return (
      <div className="alert info">
        <span className="ai"><IconInfo /></span>
        <div>
          <strong>Sandbox mode.</strong> Sending is limited to addresses verified on this
          account, capped at {me.plan?.daily_send_limit ?? 100} per day.
        </div>
      </div>
    )
  }

  return null
}

function NotFound() {
  return (
    <div className="empty">
      <div className="big">404</div>
      <p><strong>Not found</strong></p>
      <p>That page does not exist.</p>
    </div>
  )
}


function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" />
    </svg>
  )
}
